import {ChangeDetectionStrategy, Component, computed, effect, input, signal, ViewEncapsulation} from '@angular/core';
import {WpService} from '../../services/wp.service';
import {LoaderService} from '../../services/loader.service';
import {ToolsService} from '../../services/tools.service';
import {WPMediaFlat} from '../../types/WPMediaFlat';
import {IMAGE_LOADER, ImageLoaderConfig} from '@angular/common';

@Component({
  selector: 'app-media-item',
  templateUrl: './media-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: IMAGE_LOADER,
      useFactory: (component: MediaItemComponent) => {
        return (config: ImageLoaderConfig) => {
          const {width} = config;
          const widths = component.widths();
          const urlMapping = component.urlMapping();
          const availableWidths = widths.filter((w) => w >= width);
          let selectedWidth = availableWidths.length > 0 ? Math.min(...availableWidths) : Math.max(...widths);
          if (!selectedWidth) {
            selectedWidth = Math.max(...widths);
          }
          return urlMapping[selectedWidth];
        };
      },
      deps: [MediaItemComponent],
    },
  ],
})
export class MediaItemComponent {
  classList = input<string>('');
  image = input<WPMediaFlat>(null);
  routePath = input<string[]>(null);
  isPriority = input<boolean>(null);
  isNarrowColumnImage = input<boolean>(false);

  public largestImageUrl = signal<string>(null);
  public imageAlt = signal<string>(null);
  public isClickable = signal<boolean>(null);
  public largestWidth = signal<number>(null);
  public largestHeight = signal<number>(null);
  public widths = signal<number[]>([]);
  public urlMapping = signal<{[width: number]: string}>({});
  public sizes = signal<string>(null);
  public widthsSrcset = computed<string>(() => {
    return this.widths()
      .map((width) => `${width}w`)
      .join(', ');
  });
  public cssClasses = computed<string>(() => {
    let classes = this.classList();
    if (this.isClickable()) {
      classes += ' cursor-pointer';
    }
    return classes;
  });
  public loadingPriority = computed<'lazy' | 'eager' | 'auto'>(() => {
    if (this.isPriority() === null) {
      return null;
    }
    return this.isPriority() ? 'eager' : 'lazy';
  });
  public fetchPriority = computed<'high' | 'auto'>(() => {
    if (this.isPriority() === null) {
      return null;
    }
    return this.isPriority() ? 'high' : 'auto';
  });
  public shouldUsePriority = computed<boolean>(() => {
    return this.isPriority() === true;
  });

  // private subscriptions: Subscription[] = [];

  constructor(
    private wpService: WpService,
    private loaderService: LoaderService,
    private tools: ToolsService,
  ) {
    effect(
      () => {
        if (this.isNarrowColumnImage()) {
          this.sizes.set(`(min-width: 992px) 33vw, 100vw`);
        } else {
          this.sizes.set(`(min-width: 992px) 66vw, 100vw`);
        }
        if (this.image() && this.image().id > 0 && this.loadingPriority() && this.fetchPriority()) {
          this.loadImageData();
        }
        this.isClickable.set(!this.tools.isNil(this.routePath()) && this.routePath.length > 0);
      },
      {allowSignalWrites: true},
    );
  }

  // ngOnDestroy() {
  //   this.subscriptions.forEach((sub) => sub.unsubscribe());
  // }

  private loadImageData(): void {
    this.largestImageUrl.set(null);
    this.imageAlt.set(null);
    this.largestWidth.set(null);
    this.largestHeight.set(null);
    this.widths.set([]);
    this.urlMapping.set({});

    if (!this.tools.isNil(this.image()) && this.image().id > 0) {
      this.processMediaFlat(this.image());
    }
    // } else if (this.id() > 0) {
    //   const queryId: number = this.loaderService.generateQueueId();
    //   const queryItem: LoaderQueueItem = {
    //     id: queryId,
    //     title: '',
    //     description: '',
    //   };
    //   this.loaderService.addToQueue(queryItem);
    //   this.subscriptions.push(
    //     this.wpService
    //       .GetWpMediaById(this.id())
    //       .pipe(
    //         finalize(() => {
    //           this.loaderService.removeFromQueue(queryId);
    //         }),
    //       )
    //       .subscribe(
    //         (result) => this.processResults(result),
    //         (error) => console.error(error),
    //       ),
    //   );
    // }
  }

  // private processResults(result: WPMedia): void {
  //   const imageUrl: string = this.wpService.getImageUrl(result);
  //   const defaultImageUrlWebP = this.wpService.setDefaultWebPImageType(imageUrl);
  //   this.largestImageUrl.set(defaultImageUrlWebP);
  //   this.imageAlt.set(result.alt_text);
  //   this.largestWidth.set(result.media_details.width);
  //   this.largestHeight.set(result.media_details.height);
  //
  //   const sizes = result.media_details.sizes;
  //   const widths: number[] = [];
  //   const urlMapping: {[width: number]: string} = {};
  //
  //   if (!this.tools.isNil(sizes)) {
  //     Object.keys(sizes).forEach((key) => {
  //       const size: WPMediaSize = sizes[key];
  //       const width: number = size.width;
  //       const sizeUrl: string = this.wpService.setDefaultWebPImageType(
  //         this.wpService.removeWpUrlArtifacts(size.source_url),
  //       );
  //       if (!this.tools.isNil(sizeUrl) && !widths.includes(width)) {
  //         widths.push(width);
  //         urlMapping[width] = sizeUrl;
  //       }
  //     });
  //   }
  //
  //   const fullWidth = result.media_details.width;
  //   const fullUrl = this.wpService.setDefaultWebPImageType(this.wpService.removeWpUrlArtifacts(result.source_url));
  //   if (!widths.includes(fullWidth)) {
  //     widths.push(fullWidth);
  //     urlMapping[fullWidth] = fullUrl;
  //   }
  //
  //   this.widths.set(widths.sort((a, b) => a - b));
  //   this.urlMapping.set(urlMapping);
  // }

  private processMediaFlat(result: WPMediaFlat): void {
    const imageUrl: string = this.wpService.getImageFlatUrl(result);
    const defaultImageUrlWebP = this.wpService.setDefaultWebPImageType(imageUrl);
    this.largestImageUrl.set(defaultImageUrlWebP);
    this.imageAlt.set(result.alt);
    this.largestWidth.set(result.width);
    this.largestHeight.set(result.height);

    const sizes = result.sizes;
    const widths: number[] = [];
    const urlMapping: {[width: number]: string} = {};

    if (!this.tools.isNil(sizes)) {
      Object.keys(sizes).forEach((key) => {
        if (!key.endsWith('-width') && !key.endsWith('-height')) {
          const sizeUrl: string = sizes[key];
          const widthStr: string = sizes[`${key}-width`];
          const width = parseInt(widthStr, 10);
          const sizeUrlCleaned = this.wpService.setDefaultWebPImageType(this.wpService.removeWpUrlArtifacts(sizeUrl));
          if (!this.tools.isNil(sizeUrlCleaned) && !widths.includes(width)) {
            widths.push(width);
            urlMapping[width] = sizeUrlCleaned;
          }
        }
      });
    }

    const fullWidth = result.width;
    const fullUrl = this.wpService.setDefaultWebPImageType(this.wpService.removeWpUrlArtifacts(result.url));
    if (!widths.includes(fullWidth)) {
      widths.push(fullWidth);
      urlMapping[fullWidth] = fullUrl;
    }

    this.widths.set(widths.sort((a, b) => a - b));
    this.urlMapping.set(urlMapping);
  }
}
